import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, X, ChevronLeft, ChevronRight, Package } from "lucide-react";
import { getProductDetail } from "../lib/portfolio";
import ShareButton from "../components/portfolio/ShareButton";

/* /portfolio/product/:slug — shows every photo carrying this product tag
 * across all industries. Per user choice (3a), this is a clean shareable URL
 * so reps can fire a single link at a customer ("all our water bottles"). */
const PortfolioProduct = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [industryFilter, setIndustryFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const d = await getProductDetail(slug);
        if (cancelled) return;
        setProduct({ name: d.name, slug: d.slug, category: d.category, photo_count: d.photo_count });
        setPhotos(d.items || []);
        setIndustries(d.active_industries || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Product not found");
        if (e?.response?.status === 404) navigate("/portfolio");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, navigate]);

  // Client-side filter by industry (no extra request needed — all cards already loaded)
  const visiblePhotos = industryFilter
    ? photos.filter((c) => (c.industries || []).includes(industryFilter))
    : photos;

  // Lightbox keyboard nav — only photo-kind cards are eligible
  const photoCards = visiblePhotos.filter((c) => c.kind === "photo");
  const closeLightbox = useCallback(() => setLightboxIdx(null), []);
  const prevLightbox = useCallback(() => {
    setLightboxIdx((i) => (i == null ? null : (i - 1 + photoCards.length) % photoCards.length));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoCards.length]);
  const nextLightbox = useCallback(() => {
    setLightboxIdx((i) => (i == null ? null : (i + 1) % photoCards.length));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoCards.length]);

  useEffect(() => {
    if (lightboxIdx == null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") prevLightbox();
      else if (e.key === "ArrowRight") nextLightbox();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, closeLightbox, prevLightbox, nextLightbox]);

  if (loading || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--ups-paper)" }}>
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--ups-paper)" }}>
      <header className="sticky top-0 z-20 border-b" style={{ background: "#fff", borderColor: "var(--ups-border)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate("/portfolio")}
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] hover:opacity-70 transition"
            data-testid="portfolio-back-list"
          >
            <ArrowLeft size={14} /> All portfolio
          </button>
          <ShareButton testid="product-share-btn" label="Share this product link" />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[color:var(--ups-subtext)] mb-2 flex items-center gap-1.5">
            <Package size={11} /> {product.category}
          </div>
          <h1
            className="font-display text-4xl sm:text-5xl font-black leading-none tracking-tight"
            data-testid="product-title"
          >
            {product.name}
          </h1>
          <p className="mt-2 text-[13px] font-mono text-[color:var(--ups-subtext)]">
            {visiblePhotos.length} {visiblePhotos.length === 1 ? "photo" : "photos"}
            {industryFilter ? ` · ${industries.find((i) => i.slug === industryFilter)?.label || industryFilter}` : ` · across ${industries.length} ${industries.length === 1 ? "industry" : "industries"}`}
          </p>
        </div>

        {/* Industry filter chips */}
        {industries.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="industry-filter-rail">
            <button
              onClick={() => setIndustryFilter("")}
              className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm border-2 transition ${
                !industryFilter ? "bg-[color:var(--ups-ink)] text-[color:var(--ups-paper)]" : "bg-white hover:bg-[color:var(--ups-muted)]"
              }`}
              style={{ borderColor: "var(--ups-ink)" }}
              data-testid="industry-filter-all"
            >
              All industries
            </button>
            {industries.map((i) => (
              <button
                key={i.slug}
                onClick={() => setIndustryFilter(i.slug)}
                className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm border-2 transition flex items-center gap-1.5 ${
                  industryFilter === i.slug ? "bg-[#FFB500] text-[color:var(--ups-ink)]" : "bg-white hover:bg-[color:var(--ups-muted)]"
                }`}
                style={{ borderColor: industryFilter === i.slug ? "#FFB500" : "var(--ups-border)" }}
                data-testid={`industry-filter-${i.slug}`}
              >
                <span>{i.emoji}</span> {i.label}
              </button>
            ))}
          </div>
        )}

        {visiblePhotos.length === 0 ? (
          <div className="py-16 text-center text-sm text-[color:var(--ups-subtext)] border-2 border-dashed rounded-sm" style={{ borderColor: "var(--ups-border)" }} data-testid="product-empty">
            No photos match the current filter.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="photo-grid">
            {visiblePhotos.map((card, i) => {
              const isProject = card.kind === "project";
              const onClick = isProject
                ? () => navigate(`/portfolio/project/${card.id}`)
                : () => {
                    const photoIdx = visiblePhotos.slice(0, i + 1).filter((c) => c.kind === "photo").length - 1;
                    setLightboxIdx(photoIdx);
                  };
              return (
                <button
                  key={`${card.kind}-${card.id}`}
                  onClick={onClick}
                  className="relative group block aspect-square bg-stone-200 overflow-hidden border-2 hover:-translate-y-0.5 transition-transform"
                  style={{ borderColor: "var(--ups-ink)" }}
                  data-testid={isProject ? `project-${card.id}` : `photo-${card.id}`}
                  aria-label={card.name || "Portfolio item"}
                >
                  {card.cover_media_type === "video" ? (
                    <>
                      <video src={card.cover_url} muted playsInline preload="metadata" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "rgba(0,0,0,0.55)" }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z"/></svg>
                        </div>
                      </div>
                    </>
                  ) : (
                    <img src={card.cover_url} alt={card.name || "Print work"} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" referrerPolicy="no-referrer" />
                  )}
                  {isProject && (
                    <>
                      <div className="absolute top-2 left-2 px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-[0.14em] flex items-center gap-1" style={{ background: "rgba(0,0,0,0.7)", color: "#FFB500" }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M3 7h6l2 2h10v11H3z"/></svg>
                        {card.media_count}
                      </div>
                      {card.name && (
                        <div className="absolute bottom-0 left-0 right-0 px-2 py-1.5 text-[11px] font-bold leading-tight truncate" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.85), transparent)", color: "#fff" }}>
                          {card.name}
                        </div>
                      )}
                    </>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </main>

      {(() => {
        if (lightboxIdx == null || !photoCards[lightboxIdx]) return null;
        const current = photoCards[lightboxIdx].photo;
        if (!current) return null;
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.9)" }}
            onClick={closeLightbox}
            data-testid="lightbox"
          >
            <button
              onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
              className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-sm"
              aria-label="Close"
            >
              <X size={20} />
            </button>
            {photoCards.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevLightbox(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white p-2 hover:bg-white/10 rounded-sm"
                  aria-label="Previous"
                >
                  <ChevronLeft size={28} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextLightbox(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white p-2 hover:bg-white/10 rounded-sm"
                  aria-label="Next"
                >
                  <ChevronRight size={28} />
                </button>
              </>
            )}
            {current.media_type === "video" ? (
              <video src={current.image_url} controls autoPlay playsInline className="max-w-[92vw] max-h-[88vh]" onClick={(e) => e.stopPropagation()} />
            ) : (
              <img src={current.image_url} alt={current.caption || ""} className="max-w-[92vw] max-h-[88vh] object-contain" onClick={(e) => e.stopPropagation()} referrerPolicy="no-referrer" />
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default PortfolioProduct;
